class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.overlay = this.querySelector('#CartDrawer-Overlay');
        this.closeButton = this.querySelector('#CartDrawer-Close');
        this.cartDrawer = this.querySelector('#CartDrawer');
        this.cartDrawerInner = this.querySelector('.cart-drawer__inner');
        this.motionTimer = null;
        this.promotedVariantIds = [];
        this.handleEscape = (e) => {
            if (e.keyCode === 27) this.close();
        };
        this.handleOverlayClick = () => this.close();
        this.handleCloseClick = () => this.close();
        this.handleDrawerClick = (event) => {
            if (event.target.closest('#CartDrawer-Close')) {
                event.preventDefault();
                this.close();
            }
        };

        this.tl = gsap.timeline({ paused: true });
        this.closeTl = gsap.timeline({ paused: true });

        this.refreshElements();
        this.prepareMotion();
        this.setupRTLListener();
        this.eventListeners();
        this.setHeaderCartElementAccessiblity();
    }

    setHeaderCartElementAccessiblity() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');

        cartLink.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(cartLink)
        });

        cartLink.addEventListener('keydown', (event) => {
            if (event.code.toUpperCase() === 'SPACE') {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    setupRTLListener() {
        window.addEventListener('rtlChange', (event) => {
            this.prepareMotion();
        });
    }

    getSectionsToRender() {
        return [
            {
                id: 'cart-drawer',
                selector: '#CartDrawer',
            },
            {
                id: 'cart-icon-bubble'
            }
        ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    refreshElements() {
        this.overlay = this.querySelector('#CartDrawer-Overlay');
        this.closeButton = this.querySelector('#CartDrawer-Close');
        this.cartDrawer = this.querySelector('#CartDrawer');
        this.cartDrawerInner = this.querySelector('.cart-drawer__inner');
    }

    bindDrawerControls() {
        this.onkeydown = this.handleEscape;
        if (this.overlay) this.overlay.onclick = this.handleOverlayClick;
        if (this.closeButton) this.closeButton.onclick = this.handleCloseClick;
    }

    syncOpenState() {
        this.refreshElements();
        this.bindDrawerControls();

        if (!this.classList.contains('open')) return;

        gsap.killTweensOf([this, this.cartDrawerInner]);
        this.prepareMotion();
        this.tl.progress(1).pause();
        this.closeTl.pause(0).clear();
        this.classList.remove('is-closing');
        this.classList.add('motion-ready', 'open');
        this.style.visibility = 'visible';
        this.promotePinnedLineItems();
        if (this.cartDrawerInner) {
            this.cartDrawerInner.style.transform = '';
            this.cartDrawerInner.style.opacity = '';
            this.cartDrawerInner.style.visibility = '';
        }
    }

    prepareMotion() {
        this.refreshElements();
        this.classList.add('motion-ready');
        if (!this.cartDrawerInner) return;
        this.cartDrawerInner.style.transform = '';
        this.cartDrawerInner.style.opacity = '';
        this.cartDrawerInner.style.visibility = '';
    }

    waitForDrawerMotion(callback, fallback = 900) {
        window.clearTimeout(this.motionTimer);

        if (!this.cartDrawerInner) {
            callback?.();
            return;
        }

        let done = false;
        const finish = (event) => {
            if (event && event.target !== this.cartDrawerInner) return;
            if (done) return;
            done = true;
            this.cartDrawerInner.removeEventListener('transitionend', finish);
            window.clearTimeout(this.motionTimer);
            callback?.();
        };

        this.cartDrawerInner.addEventListener('transitionend', finish);
        this.motionTimer = window.setTimeout(finish, fallback);
    }

    markUpdating() {
        this.refreshElements();
        this.classList.add('is-updating');
        if (this.cartDrawerInner) this.cartDrawerInner.classList.add('is-refreshing');
    }

    clearUpdating() {
        this.refreshElements();
        this.classList.remove('is-updating', 'is-pending-update');
        if (!this.cartDrawerInner) return;
        this.cartDrawerInner.classList.remove('is-refreshing');
        this.cartDrawerInner.classList.add('is-hydrating');
        window.setTimeout(() => {
            this.cartDrawerInner?.classList.remove('is-hydrating');
        }, 360);
    }

    showPending(triggeredBy) {
        this.markUpdating();
        if (this.classList.contains('open')) return;
        this.open(triggeredBy);
        this.classList.add('is-pending-update');
    }

    scrollToFirstItem(options = {}) {
        this.refreshElements();
        if (!this.cartDrawerInner) return;

        const behavior = options.smooth === false ? 'auto' : 'smooth';
        this.cartDrawerInner.scrollTo({
            top: 0,
            behavior,
        });
    }

    promoteLineItem(variantId) {
        if (!variantId) return;

        this.refreshElements();
        const safeVariantId = String(variantId).replace(/"/g, '');
        const item = this.querySelector(`.cart-item[data-variant-id="${safeVariantId}"]`);
        const parent = item?.parentElement;

        if (!item || !parent || parent.firstElementChild === item) return;
        parent.prepend(item);
    }

    rememberPromotedLineItem(variantId) {
        if (!variantId) return;
        const safeVariantId = String(variantId);
        this.promotedVariantIds = this.promotedVariantIds.filter((id) => id !== safeVariantId);
        this.promotedVariantIds.unshift(safeVariantId);
    }

    promotePinnedLineItems() {
        if (!this.promotedVariantIds?.length) return;

        const availableIds = new Set(
            Array.from(this.querySelectorAll('.cart-item[data-variant-id]')).map((item) => item.dataset.variantId)
        );
        this.promotedVariantIds = this.promotedVariantIds.filter((id) => availableIds.has(String(id)));

        for (let index = this.promotedVariantIds.length - 1; index >= 0; index -= 1) {
            this.promoteLineItem(this.promotedVariantIds[index]);
        }
    }

    animate(isRTL = window.app.isRTL()) {
        this.tl.clear();
        if (!this.cartDrawerInner) return;

        const startX = isRTL ? '-104%' : '104%';

        this.tl
            .set(this.cartDrawerInner, {
                force3D: true,
                willChange: 'transform',
            }, 0)
            .fromTo(this,
                {
                    visibility: 'hidden',
                },
                {
                    visibility: 'visible',
                    duration: 0.01,
                    ease: 'none'
                }, 0)
            .fromTo(this.cartDrawerInner,
                {
                    x: startX,
                    autoAlpha: 0.96,
                },
                {
                    x: '0%',
                    autoAlpha: 1,
                    duration: 0.86,
                    ease: 'sine.out',
                    force3D: true,
                    overwrite: 'auto',
                }, 0)
    }

    animateClose(onComplete, isRTL = window.app.isRTL()) {
        if (!this.cartDrawerInner) {
            onComplete?.();
            return;
        }

        const endX = isRTL ? '-104%' : '104%';

        gsap.killTweensOf([this.cartDrawerInner, this.tl]);
        this.closeTl.pause(0).clear();
        this.closeTl
            .set(this.cartDrawerInner, {
                force3D: true,
                willChange: 'transform',
            }, 0)
            .to(this.cartDrawerInner, {
                x: endX,
                duration: 0.62,
                ease: 'sine.inOut',
                force3D: true,
                overwrite: 'auto',
            }, 0)
            .to(this, {
                visibility: 'hidden',
                duration: 0.01,
                ease: 'none',
            }, 0.61)
            .eventCallback('onComplete', onComplete)
            .play(0);
    }

    lockBodyScroll() {
        if (window.SkinNoteDrawers) {
            window.SkinNoteDrawers.lock('cart');
            return;
        }
        if (document.body.classList.contains('cart-drawer-body-locked')) return;

        this.scrollY = window.scrollY || window.pageYOffset || 0;

        document.body.classList.add('cart-drawer-body-locked');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
    }

    unlockBodyScroll() {
        if (window.SkinNoteDrawers) {
            window.SkinNoteDrawers.unlock('cart');
            return;
        }
        if (!document.body.classList.contains('cart-drawer-body-locked')) return;

        const scrollY = this.scrollY || 0;
        const previousScrollBehavior = document.documentElement.style.scrollBehavior;

        document.documentElement.style.scrollBehavior = 'auto';

        document.body.classList.remove('cart-drawer-body-locked');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';

        window.scrollTo(0, scrollY);

        requestAnimationFrame(() => {
            document.documentElement.style.scrollBehavior = previousScrollBehavior;
        });
    }

    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        if (window.SkinNoteSearchModal?.isOpen || document.body.classList.contains('search-modal-open')) {
            window.SkinNoteSearchModal?.close?.(true);
        }

        const headerDrawer = document.querySelector('header-drawer');
        if (headerDrawer?.isDrawerOpen || document.body.classList.contains('menu-drawer-open')) {
            headerDrawer.hideDrawer?.();
        }

        this.refreshElements();
        this.bindDrawerControls();
        this.prepareMotion();
        this.lockBodyScroll();
        this.classList.remove('is-closing');
        this.style.visibility = 'visible';

        requestAnimationFrame(() => {
            this.closeTl.pause(0).clear();
            if (this.cartDrawerInner) this.cartDrawerInner.getBoundingClientRect();
            this.classList.add('open');
            this.tl.progress(1).pause();
            this.waitForDrawerMotion(() => {
                const containerToTrapFocus = this.cartDrawer;
                const focusElement = document.querySelector('.cart-drawer__inner') || this.closeButton;
                trapFocus(containerToTrapFocus, focusElement);
            }, 1250);
        });

        document.body.classList.add('cart-drawer-open', 'overflow-hidden');
    }

    close() {
        this.refreshElements();
        if (!this.classList.contains('open') && !this.classList.contains('is-closing')) return;

        let didClose = false;
        const finishClose = () => {
            if (didClose) return;
            didClose = true;
            this.classList.remove('open');
            this.classList.remove('is-closing');
            this.style.visibility = 'hidden';
            this.unlockBodyScroll();
        };

        this.prepareMotion();
        this.classList.add('is-closing');
        this.waitForDrawerMotion(finishClose, 1100);
        this.closeTl.progress(1).pause();

        removeTrapFocus(this.activeElement);
        document.body.classList.remove('cart-drawer-open', 'overflow-hidden');

        window.setTimeout(finishClose, 1150);
    }

    renderContents(parsedState, options = {}) {
        const preserveOpen = options.preserveOpen && this.classList.contains('open');
        const scrollToTop = options.scrollToTop === true;
        const promotedVariantId = options.promotedVariantId || parsedState.id || parsedState.items?.[0]?.id;
        const previousScrollTop = this.cartDrawerInner?.scrollTop || 0;

        this.markUpdating();
        this.cartDrawerInner.classList.contains('is-empty') && this.cartDrawerInner.classList.remove('is-empty');
        this.productId = promotedVariantId;
        this.rememberPromotedLineItem(promotedVariantId);

        this.getSectionsToRender().forEach((section => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }));

        requestAnimationFrame(() => {
            this.refreshElements();
            this.bindDrawerControls();
            this.promotePinnedLineItems();
            if (preserveOpen) {
                this.syncOpenState();
                if (scrollToTop) {
                    requestAnimationFrame(() => this.scrollToFirstItem());
                } else if (this.cartDrawerInner) {
                    this.cartDrawerInner.scrollTop = previousScrollTop;
                }
                this.clearUpdating();
                document.body.classList.add('cart-drawer-open', 'overflow-hidden');
                return;
            }
            this.animate();
            this.open();
            if (scrollToTop) requestAnimationFrame(() => this.scrollToFirstItem({ smooth: false }));
            this.clearUpdating();
        });
    }

    eventListeners() {
        this.addEventListener('click', this.handleDrawerClick);
        this.bindDrawerControls();
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerContent extends CartForm {
    getSectionsToRender() {
        return [
            {
                id: 'cart-icon-bubble',
                section: 'cart-icon-bubble',
                selector: '.shopify-section',
            },
            {
                id: 'CartDrawer',
                section: 'cart-drawer',
                selector: '.cart-drawer__inner',
            },
        ]
    }
}

customElements.define('cart-drawer-content', CartDrawerContent);
